<template>
  <div class="messages__document">
    <LennocFileRow
      v-if="isUserDocument"
      :fileRow="[Document]"
      @deletedFile="deleteFile" />
    <div class="aspect-ratio aspect-ratio--16-9" v-if="hasVideo">
      <iframe :src="videoSource" frameborder="0" allowfullscreen rel="0"></iframe>
    </div>
    <claim-email v-if="hasEmail" :documentId="documentId" @show-file="showFile" :documentAction="documentAction"></claim-email>
    <base-flex-message
      v-if="!hasVideo"
      :message="documentMessage"
      :opentext="getUiString(['claim_details','claim','read_more'])"
      :closetext="getUiString(['claim_details','claim','read_less'])"
    ></base-flex-message>
    <a
      v-if="hasPoa"
      @click="showFile(Document)"
      class
    >{{ getUiString(['claim_details', 'claim', 'view_poa_document']) }}</a>
    <button
      v-if="hasGenericFile"
      @click="showFile(Document)"
      class="button button--sec"
    >{{ getUiString(['claim_details', 'claim', 'view_file']) }}</button>
    <button
      v-if="hasDocuSign"
      @click="showFile(nestedDocument)"
      class="button button--sec"
    >{{ getUiString(['claim_details', 'claim', 'view_poa_document']) }}</button>
    <div class="messages--temporary" v-if="isFreshMessage">
      <lennoc-icon image="check--large" />
      <span>{{ getUiString(['claim_details', 'claim', 'message_sent_text']) }}</span>
    </div>
    <claim-bank-details v-if="isBankDataRequest"></claim-bank-details>
    <base-button-group
      v-if="needsConfirmation"
      :buttons="confirmationButtons"
      :markClicked="true"
      @button-click="confirmReadMessage"
    ></base-button-group>
    <claim-request-item
      v-if="isClaimRequest"
      :isHandled="isHandled"
      :linkedClaim="relatedClaimId"
      @claim-submitted="handleClaimRequest"
    ></claim-request-item>
    <base-file-view v-if="viewerData" :document="viewerData" @close="viewerData = null"></base-file-view>
    <LennocSpinner isFullscreen="false"  v-if="busy" />
  </div>
</template>

<script>
import day from 'dayjs'
import { saveAs } from 'file-saver'
import ClaimRequestItem from '@/components/claims/ClaimRequestItem.vue'
import ClaimEmail from '@/components/claims/ClaimEmail.vue'
import ClaimBankDetails from '@/components/claims/ClaimBankDetails.vue'
import { mapGetters } from 'vuex'

const EMBEDDED_NONE = 0
const EMBEDDED_GENERIC = 1
const EMBEDDED_EMAIL = 2
const EMBEDDED_POA = 3
const EMBEDDED_DOCUSIGN = 4

export default {
  name: 'ClaimDocument',
  components: {
    ClaimEmail,
    ClaimRequestItem,
    ClaimBankDetails
  },
  props: {
    documentId: {
      type: String,
      required: true
    },
    relatedClaimId: {
      type: String,
      default () {
        return ''
      }
    },
    hasTask: {
      type: Boolean,
      default () {
        return false
      }
    }
  },
  data () {
    return {
      busy: false,
      Document: {},
      viewerData: null,
      isFreshMessage: false,
      isClaimRequestHandled: false
    }
  },
  mounted () {
    this.Document = this.getDocumentById(this.documentId)
    if (this.isUserMessage) {
      if (day(this.Document.TimeStamp).isAfter(day().subtract(2, 'minute'))) {
        setTimeout(() => {
          this.isFreshMessage = false
        }, 5000)
        this.isFreshMessage = true
      }
    }
  },
  computed: {
    ...mapGetters({
      getDocumentById: 'claim/getDocumentById',
      getUiString: 'uistrings/getUiString'
    }),
    documentName () {
      return this.Document.Name || ''
    },
    documentMessage () {
      return this.Document.Message || ''
    },
    documentDescription () {
      return this.Document.Description || ''
    },
    documentAction () {
      return this.Document.DocumentAction || ''
    },
    isUserDocument () {
      return this.documentAction === 'DocumentCustomer' && this.hasFile
    },
    hasFile () {
      return this.Document.ContainsFile || false
    },
    documentActionPerformedDateTime () {
      return this.Document.DocumentActionPerformedDateTime || null
    },
    hasVideo () {
      return this.documentMessage.indexOf('[video]') === 0
    },
    videoSource () {
      if (this.documentMessage) {
        const match = this.documentMessage.match(/\[video](.+?)\[\/video]/)
        return match ? match[1] : ''
      }
      return ''
    },
    documentFileType () {
      if (
        this.documentAction === 'RequestUploadDocument' &&
        this.documentDescription.indexOf('Power of Attorney') > -1
      ) {
        return EMBEDDED_POA
      }
      if (
        this.documentDescription.indexOf('.msg') > -1 || this.documentName.indexOf('.msg') > -1 || this.documentName.indexOf('.eml') > -1
      ) {
        return EMBEDDED_EMAIL
      }
      if (this.hasFile && this.documentAction !== 'DocumentCustomer') {
        return EMBEDDED_GENERIC
      }
      if (this.nestedDocument.ContainsFile && this.documentAction === 'RequestCompleteDocuSign') {
        return EMBEDDED_DOCUSIGN
      }
      return EMBEDDED_NONE
    },
    hasEmail () { return this.documentFileType === EMBEDDED_EMAIL },
    hasPoa () { return this.documentFileType === EMBEDDED_POA },
    hasGenericFile () { return this.documentFileType === EMBEDDED_GENERIC },
    hasDocuSign () { return this.documentFileType === EMBEDDED_DOCUSIGN },
    nestedDocument () {
      return this.Document.Children && this.Document.Children.length === 1 ? this.Document.Children[0] : {}
    },
    needsConfirmation () {
      return this.documentAction === 'RequestMarkRead' && !this.Document.DocumentActionPerformedDateTime
    },
    confirmationButtons () {
      return [
        {
          id: 'confirm_0',
          text: this.getUiString(['claim_details', 'claim', 'mark_as_read'])
        }
      ]
    },
    isClaimRequest () {
      return this.documentAction === 'RequestClaim'
    },
    isBankDataRequest () {
      return this.documentAction === 'RequestUpdateBankDetails'
    },
    isHandled () {
      return !!this.documentActionPerformedDateTime && !!this.relatedClaimId
    }
  },
  methods: {
    async deleteFile (fileId) {
      this.busy = true
      await this.$store.dispatch('claim/deleteDocument', { documentId: fileId })
      await this.$store.dispatch('claim/refreshClaim')
      this.busy = false
    },
    isImage (type) {
      return type.indexOf('image') === 0 ||
      type.indexOf('pdf') > -1
    },
    showFile (doc) {
      if (this.isImage(doc.ContentType)) {
        this.viewerData = {
          index: doc.index,
          fileId: doc.Id,
          fileType: doc.ContentType || '',
          fileName: doc.Name
        }
      } else {
        this.downloadFile(doc)
      }
    },
    downloadFile (doc) {
      this.startDownload(doc).then(response => {
        saveAs(response.data, doc.Name)
      }).finally(() => { this.busy = false })
    },
    async startDownload (doc) {
      if (doc.index > -1) {
        return this.$store.dispatch('claim/downloadAttachment', {
          index: doc.index,
          documentId: doc.Id,
          fileType: doc.ContentType,
          fileName: doc.Name
        })
      }
      return this.$store.dispatch('downloadFile', {
        fileId: doc.Id,
        fileType: doc.ContentType,
        fileName: doc.Name
      })
    },
    handleClaimRequest () {
      if (this.hasTask) {
        this.$store
          .dispatch('claim/confirmAction', { documentId: this.Document.Id })
          .then(() => {
            this.$store.dispatch('claim/refreshClaim')
          })
      }
    },
    confirmReadMessage () {
      this.$store
        .dispatch('claim/confirmAction', { documentId: this.Document.Id })
        .then(() => {
          this.$store.dispatch('claim/refreshClaim')
        })
    }
  }
}
</script>
